import { useMemo, useCallback } from 'react';
import {
  CM_PRIMARY_ID,
  ELECTION_TYPE,
  NOMINATION_STATUS,
  SE_COMMITTEE_ID,
} from '../../utils/constants';

export const useCouncilMemberFiltering = ({
  showCouncilMembersOnly,
  councilMembersArray,
  nominationArray,
  candidates,
  committeeMembers,
  committees,
}) => {
  const councilMemberDetails = useMemo(() => {
    if (!Array.isArray(councilMembersArray)) return [];

    return councilMembersArray.map((cm) => {
      const userId = cm.attributes?.users_permissions_user?.data?.id;
      const userAttrs = cm.attributes?.users_permissions_user?.data?.attributes;
      return {
        councilMemberId: cm.id,
        userId,
        username: userAttrs?.username,
        email: userAttrs?.email,
        fellowId: userAttrs?.fellowId,
      };
    });
  }, [councilMembersArray]);

  const createSyntheticMember = useCallback(
    (cm, committeeId = null) => ({
      id: cm.userId,
      name: cm.username
        ? cm.username.replace(/\s*\([^)]*\)/, '').trim()
        : 'Unknown',
      isChair: false,
      committeeId,
      userId: cm.userId,
    }),
    [],
  );

  const councilShortlistedCandidatesByCommittee = useMemo(() => {
    if (!showCouncilMembersOnly || !Array.isArray(nominationArray)) {
      return new Map();
    }

    const map = new Map();

    nominationArray.forEach((nomination) => {
      const {
        nominationStatusTypeId,
        candidateId,
        sectionalCommitteeId,
        electionTypeId,
      } = nomination.attributes || {};

      if (
        nominationStatusTypeId === NOMINATION_STATUS.RECOMMENDED &&
        candidateId
      ) {
        let committeeId = sectionalCommitteeId;

        if (!committeeId) {
          if (electionTypeId === ELECTION_TYPE.CORRESPONDING_MEMBER) {
            committeeId = CM_PRIMARY_ID;
          } else if (electionTypeId === ELECTION_TYPE.SPECIAL_ELECTION) {
            committeeId = SE_COMMITTEE_ID;
          }
        }

        if (committeeId) {
          if (!map.has(committeeId)) {
            map.set(committeeId, new Set());
          }
          map.get(committeeId).add(candidateId);
        }
      }
    });

    return map;
  }, [showCouncilMembersOnly, nominationArray]);

  const councilShortlistedCandidateIds = useMemo(() => {
    if (!showCouncilMembersOnly) return new Set();

    const ids = new Set();
    councilShortlistedCandidatesByCommittee.forEach((candidateSet) => {
      candidateSet.forEach((id) => ids.add(id));
    });
    return ids;
  }, [showCouncilMembersOnly, councilShortlistedCandidatesByCommittee]);

  const filteredCandidates = useMemo(() => {
    if (!showCouncilMembersOnly) return candidates;

    return candidates.filter((candidate) =>
      councilShortlistedCandidateIds.has(candidate.id),
    );
  }, [candidates, showCouncilMembersOnly, councilShortlistedCandidateIds]);

  const filteredCommitteeMembers = useMemo(() => {
    if (!showCouncilMembersOnly) return committeeMembers;

    return councilMemberDetails.map((cm) => createSyntheticMember(cm));
  }, [
    showCouncilMembersOnly,
    committeeMembers,
    councilMemberDetails,
    createSyntheticMember,
  ]);

  const filteredCommittees = useMemo(() => {
    if (!showCouncilMembersOnly) return committees;

    const allCouncilMembers = councilMemberDetails.map((cm) =>
      createSyntheticMember(cm),
    );

    return committees
      .map((committee) => {
        const councilShortlistedIds =
          councilShortlistedCandidatesByCommittee.get(committee.id);

        if (!councilShortlistedIds?.size) return null;

        const filteredCandidatesForCommittee = committee.candidates.filter(
          (candidate) => councilShortlistedIds.has(candidate.id),
        );

        return {
          ...committee,
          committeeMembers: allCouncilMembers,
          candidates: filteredCandidatesForCommittee,
        };
      })
      .filter(Boolean);
  }, [
    showCouncilMembersOnly,
    committees,
    councilMemberDetails,
    councilShortlistedCandidatesByCommittee,
    createSyntheticMember,
  ]);

  return {
    filteredCandidates,
    filteredCommitteeMembers,
    filteredCommittees,
  };
};
